/**
 * Shared formatting utilities used across StatCard, SignalRow, PlanCard,
 * and any other component displaying prices, percentages, counts, or
 * timestamps. Centralised here so number/date formatting stays consistent
 * platform-wide rather than reimplemented per-component.
 *
 * No date library dependency — relative-time formatting is small enough
 * to hand-write correctly for this product's range of timestamps (signals
 * and setting changes are always recent — seconds to weeks old).
 */

/**
 * Formats a timestamp as a short relative string: "2m ago", "3h ago",
 * "5d ago". Falls back to a locale date string beyond 30 days, since
 * "47d ago" is less useful than an actual date at that range.
 */
export function formatRelativeTime(isoString: string, now: Date = new Date()): string {
  const then = new Date(isoString);
  const diffMs = now.getTime() - then.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 5) return "just now";
  if (diffSeconds < 60) return `${diffSeconds}s ago`;

  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays}d ago`;

  return then.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * Formats a 0–1 decimal as a whole-number percentage string: 0.7342 -> "73%".
 * Pass `precision` for decimal places when more granularity is needed
 * (e.g. win rate trends): formatPercentage(0.6423, 1) -> "64.2%".
 */
export function formatPercentage(value: number, precision = 0): string {
  return `${(value * 100).toFixed(precision)}%`;
}

/**
 * Formats a monetary amount with currency symbol. Defaults to USD since
 * that is the only currency seeded in plans.currency at launch (migration
 * 20260618001100); the currency code itself remains data-driven, not
 * hardcoded — this formatter just needs an Intl.NumberFormat-compatible
 * code, whatever the database returns.
 */
export function formatCurrency(amount: number, currencyCode = "USD"): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Formats a signed pip/point delta with an explicit + or - sign, since
 * trading data conventionally shows direction even for positive values
 * (e.g. "+59 pips", "-32 pips").
 */
export function formatSignedPips(pips: number): string {
  const sign = pips > 0 ? "+" : "";
  return `${sign}${pips.toFixed(0)} pips`;
}

/**
 * Formats a signed percentage delta with an explicit + or - sign, used
 * for P&L and trend deltas (e.g. "+1.8%", "-0.4%").
 */
export function formatSignedPercentage(value: number, precision = 1): string {
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(precision)}%`;
}

/**
 * Formats a price value with a fixed number of decimal places, matching
 * the precision convention of the asset's pip_size — defaults to 4 decimal
 * places (standard Forex quote precision) since pip_size is not always
 * available at the call site. Callers with pip_size should pass an
 * explicit precision derived from it.
 */
export function formatPrice(value: number, precision = 4): string {
  return value.toFixed(precision);
}

/**
 * Formats a plain integer count with locale thousands separators.
 */
export function formatCount(value: number): string {
  return new Intl.NumberFormat(undefined).format(value);
}

/**
 * Returns the appropriate directional CSS class name ("text-positive" /
 * "text-negative" / "text-neutral") for a signed numeric value, so
 * components can color P&L/delta text consistently with the design
 * token system without each component re-implementing the sign check.
 * Note: no .text-positive/.text-negative utility classes exist in
 * global.css yet — components needing this should apply
 * var(--positive-500)/var(--negative-500) inline via style, or a future
 * Phase 0 follow-up can add the utility classes if this pattern recurs
 * widely enough to warrant it.
 */
export function getDirectionalTone(value: number): "positive" | "negative" | "neutral" {
  if (value > 0) return "positive";
  if (value < 0) return "negative";
  return "neutral";
}

// ---------------------------------------------------------------------------
// Short-form aliases used by the Admin Console.  Centralised here so every
// admin page gets the same formatting behaviour without re-implementing it.
// ---------------------------------------------------------------------------

/**
 * Formats an ISO timestamp as an absolute, human-readable date+time string:
 * "Jun 22, 2026, 14:32".  Admin tables display absolute times (not relative)
 * because log/event rows can be hours or days old.
 */
export function fmtDateTime(isoString: string | null | undefined): string {
  if (!isoString) return "—";
  return new Date(isoString).toLocaleString(undefined, {
    year:   "numeric",
    month:  "short",
    day:    "numeric",
    hour:   "2-digit",
    minute: "2-digit",
  });
}

/**
 * Short alias for formatPercentage — renders a 0–1 fraction as "70%".
 * Accepts an optional precision argument (default 0) matching the
 * existing formatPercentage signature.
 */
export function fmtPct(value: number, precision = 0): string {
  return formatPercentage(value, precision);
}

/**
 * Short alias for formatPrice — renders a number with a fixed number of
 * decimal places (default 4, matching the Forex pip precision convention).
 */
export function fmtNum(value: number, precision = 4): string {
  return formatPrice(value, precision);
}
